import * as React from 'react';

import { EmblaCarouselType } from 'embla-carousel';

const useClickAway = (callback: () => void, exclude?: string) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const refCb = React.useRef(callback);

  React.useLayoutEffect(() => {
    refCb.current = callback;
  });

  React.useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && e.target && !ref.current.contains(e.target as Node)) {
        if (exclude) {
          if ((e.target as HTMLElement).ariaLabel !== exclude) {
            refCb.current();
          }
        } else {
          refCb.current();
        }
      }
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [exclude]);

  return ref;
};

const useMeasure = () => {
  const [dimensions, setDimensions] = React.useState({
    width: 0,
    height: 0,
  });

  const previousObserver = React.useRef<ResizeObserver | null>(null);

  const customRef = React.useCallback((node: Element | null) => {
    if (previousObserver.current) {
      previousObserver.current.disconnect();
      previousObserver.current = null;
    }

    if (node?.nodeType === Node.ELEMENT_NODE) {
      const observer = new ResizeObserver(([entry]) => {
        if (entry?.borderBoxSize) {
          const { inlineSize: width, blockSize: height } =
            entry.borderBoxSize[0];
          setDimensions({ width, height });
        }
      });

      observer.observe(node);
      previousObserver.current = observer;
    }
  }, []);

  return [customRef, dimensions] as const;
};

const useWindowSize = () => {
  const [size, setSize] = React.useState({
    width: 0,
    height: 0,
  });

  React.useLayoutEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.visualViewport?.width ?? window.screen.width,
        height: window.visualViewport?.height ?? window.screen.height,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return size;
};

const useDevice = (dimensions: { width: number; height: number }) => {
  if (dimensions.width > 768) return 'desktop';
  if (dimensions.width <= 768) return 'mobile';
};

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
): {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
} => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<Array<number>>([]);

  const onDotButtonClick = React.useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = React.useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = React.useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  React.useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export { useClickAway, useMeasure, useDevice, useWindowSize, useDotButton };
